import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUsersListRequest,
  setSearchTerm as setSearchTermAction,
  setActiveFilter as setActiveFilterAction,
  clearFilters as clearFiltersAction,
  clearSuccessMessage,
} from '_admin/features/users/usersSlice';
import {
  selectAllUsers,
  selectTotalUsersCount,
  selectUsersLoading,
  selectListError,
  selectSuccessMessage,
  selectSearchTerm,
  selectActiveFilter,
} from '_admin/features/users/usersSelectors';
import { enrichUser } from '_admin/features/users/UserService';
import useLiveRefresh from '@utils/useLiveRefresh';

export const useUserList = ({ loggedInUserId = null, itemsPerPage = 100 } = {}) => {
  const dispatch = useDispatch();

  const allUsers = useSelector(selectAllUsers);
  const totalCount = useSelector(selectTotalUsersCount);
  const loading = useSelector(selectUsersLoading);
  const listError = useSelector(selectListError);
  const successMessage = useSelector(selectSuccessMessage);
  const searchTerm = useSelector(selectSearchTerm);
  const activeFilter = useSelector(selectActiveFilter);

  const [currentPage, setCurrentPage] = useState(0);
  // Sorting is not required for now — kept commented out as a future
  // requirement. Re-enable `sortOrder`/`handleSortToggle` (and the `order`/
  // `sort_by` fields in `doFetch`) when sorting is actually needed.
  // const [sortOrder, setSortOrder] = useState(true);

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const debounceRef = useRef(null);

  const latestSearchRef = useRef('');
  const latestFilterRef = useRef(activeFilter);
  // const latestSortRef = useRef(sortOrder);
  latestSearchRef.current = localSearchTerm;
  latestFilterRef.current = activeFilter;
  // latestSortRef.current = sortOrder;

  const getFilterParams = useCallback((filter) => {
    switch (filter) {
      case 'Active':    return { active: true };
      case 'Blocked':   return { deleted: true };
      case 'Temporary': return { temp_user: true };
      case 'All':
      default:          return {};
    }
  }, []);

  // `sort` param plumbing left in place (commented) for when sorting comes
  // back — see the `sortOrder` note above.
  // Guards against a `user.*` live-refresh event (e.g. the broadcast from the
  // admin's own save) landing right after a fetch we already triggered
  // ourselves (mount, or returning from edit) — without this, the two race
  // and the list fetches twice in quick succession.
  const lastFetchAtRef = useRef(0);
  const LIVE_REFRESH_DEDUPE_MS = 3000;

  const doFetch = useCallback(({ search = '', filter = 'Active', page = 0 /* , sort = sortOrder */ } = {}) => {
    const fetchParams = {
      page_number: page + 1,
      page_size: itemsPerPage,
      // sort_by: 'first_name',
      // order: sort,
    };

    if (search && search.trim()) {
      fetchParams.search_columns = 'email,first_name,last_name,title';
      fetchParams.search_string = search.trim();
    } else {
      Object.assign(fetchParams, getFilterParams(filter));
    }

    lastFetchAtRef.current = Date.now();
    dispatch(fetchUsersListRequest(fetchParams));
  }, [dispatch, itemsPerPage, getFilterParams]);

  useEffect(() => {
    doFetch({ filter: activeFilter });
  }, []);

  // A user.* event from bifrost means someone else changed a user in this org.
  // The current search, filter and page are replayed rather than reset, so the
  // list the admin is looking at does not jump underneath them.
  useLiveRefresh(['user'], () => {
    if (Date.now() - lastFetchAtRef.current < LIVE_REFRESH_DEDUPE_MS) return;
    doFetch({
      search: latestSearchRef.current,
      filter: latestFilterRef.current,
      page: currentPage,
    });
  });

  // const handleSortToggle = useCallback(() => {
  //   setSortOrder((prev) => {
  //     const next = !prev;
  //     doFetch({ search: latestSearchRef.current, filter: latestFilterRef.current, page: currentPage, sort: next });
  //     return next;
  //   });
  // }, [doFetch, currentPage]);

  const handleSearchChange = useCallback((value) => {
    setLocalSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(setSearchTermAction(value));
      if (value && value.trim()) {
        dispatch(setActiveFilterAction('All'));
        setCurrentPage(0);
        doFetch({ search: value, filter: 'All', page: 0 });
      } else {
        dispatch(setActiveFilterAction('Active'));
        setCurrentPage(0);
        doFetch({ search: '', filter: 'Active', page: 0 });
      }
    }, 400);
  }, [dispatch, doFetch]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      dispatch(setSearchTermAction(''));
      if (latestSearchRef.current && latestSearchRef.current.trim()) {
        dispatch(setActiveFilterAction('Active'));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearSearch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLocalSearchTerm('');
    dispatch(setSearchTermAction(''));
    dispatch(setActiveFilterAction('Active'));
    setCurrentPage(0);
    doFetch({ search: '', filter: 'Active', page: 0 });
  }, [dispatch, doFetch]);

  const handleFilterChange = useCallback((filter) => {
    setLocalSearchTerm('');
    dispatch(setSearchTermAction(''));
    dispatch(setActiveFilterAction(filter));
    setCurrentPage(0);
    doFetch({ search: '', filter, page: 0 });
  }, [dispatch, doFetch]);

  const handleClearFilters = useCallback(() => {
    dispatch(clearFiltersAction());
    setLocalSearchTerm('');
    setCurrentPage(0);
    doFetch({ search: '', filter: 'Active', page: 0 });
  }, [dispatch, doFetch]);

  const refreshUsers = useCallback(() => {
    setLocalSearchTerm('');
    dispatch(setSearchTermAction(''));
    setCurrentPage(0);
    doFetch({ search: '', filter: activeFilter, page: 0 });
  }, [dispatch, doFetch, activeFilter]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => dispatch(clearSuccessMessage()), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  const enrichedUsers = useMemo(() => {
    let users = allUsers || [];
    if (activeFilter === 'Active') {
      users = users.filter((u) => u.isActive);
    }
    return loggedInUserId
      ? users.map((user) => enrichUser(user, loggedInUserId))
      : users;
  }, [allUsers, activeFilter, loggedInUserId]);

  const totalPages = Math.ceil((totalCount || 0) / itemsPerPage);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => {
      const newPage = Math.min(prev + 1, totalPages - 1);
      doFetch({ search: latestSearchRef.current, filter: latestFilterRef.current, page: newPage });
      return newPage;
    });
  }, [totalPages, doFetch]);

  const goToPrevPage = useCallback(() => {
    setCurrentPage((prev) => {
      const newPage = Math.max(prev - 1, 0);
      doFetch({ search: latestSearchRef.current, filter: latestFilterRef.current, page: newPage });
      return newPage;
    });
  }, [doFetch]);

  const goToPage = useCallback((page) => {
    const clamped = Math.max(0, Math.min(page, totalPages - 1));
    setCurrentPage(clamped);
    doFetch({ search: latestSearchRef.current, filter: latestFilterRef.current, page: clamped });
  }, [totalPages, doFetch]);

  return {
    users: enrichedUsers,
    allFilteredUsers: allUsers,
    totalCount,
    loading,
    listError,
    successMessage,

    searchTerm: localSearchTerm,
    activeFilter,
    handleSearchChange,
    handleClearSearch,
    handleFilterChange,
    handleClearFilters,

    // sortOrder,
    // handleSortToggle,

    currentPage,
    totalPages,
    itemsPerPage,
    goToNextPage,
    goToPrevPage,
    goToPage,
    setCurrentPage,

    doFetch,
    refreshUsers,
  };
};
